import NodeSelect from "@components/NodeSelect"
import { getNodeSelectOptions } from "@components/NodeSelect/helpers"
import { NodeResult, NodeResults } from "@custom-types/modelRun"
import { GroupNodeLabel } from "@features/graph/graphViewModel"
import { ThresholdFormValues } from "@features/results/Result/ThresholdChartContainer/ThresholdForm"
import BoundarySlider from "@features/results/Result/ThresholdChartContainer/ThresholdForm/BoundarySlider"
import {
  getTimestepMinMaxValues,
  getTimestepOptions,
} from "@features/results/Result/ThresholdChartContainer/ThresholdForm/helpers"
import { Autocomplete, FormControlLabel, Grid, Switch, TextField } from "@mui/material"
import { GraphNodeData, GraphNodeEdgeData } from "@store/services/converter/types"
import { useEffect } from "react"
import { Controller, useFormContext } from "react-hook-form"
import { graphViewGroupNodeLabelToSampleNode } from "@features/graph/utils/graphUtils"

type EventFieldsProp = {
  graphData: GraphNodeEdgeData
  prefixName: keyof ThresholdFormValues
  nodesData: GraphNodeData[]
  nodesResults: NodeResults
  showMinMaxBoundary?: boolean
  lag: number
}

const EventFields = (props: EventFieldsProp) => {
  const form = useFormContext<ThresholdFormValues>()

  const nodeValue = form.watch(`${props.prefixName}.node`)
  const timestepValue = form.watch(`${props.prefixName}.timestep`)

  const resetBoundaryField = () => {
    const { min, max } = getTimestepMinMaxValues(timestepValue, nodeResults)
    const avg = Number.parseFloat(((min + max) / 2).toFixed(3))
    form.setValue(`${props.prefixName}.boundary`, avg)
  }

  // reset timestep option after changing node
  useEffect(() => {
    const newTimestep = getTimestepOptions(nodeResults, props.lag)[0]
    // @ts-expect-error Types needs fixing (value is possibly undefined) - temp bypass for bulk migration
    form.setValue(`${props.prefixName}.timestep`, newTimestep)

    // manually update boundary field as timestep is the same, so useEffect listening for its change won't be invkoed
    if (timestepValue === newTimestep) {
      resetBoundaryField()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- FIX: deps after bulk migration
  }, [nodeValue])

  // reset boundary value after changing timestep
  useEffect(
    () => {
      resetBoundaryField()
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps -- FIX: deps after bulk migration
    [timestepValue],
  )

  // guarantee data is orderly
  let nodeResults: NodeResult[] = []
  if (props.nodesResults[nodeValue] !== undefined) {
    nodeResults = Object.values(props.nodesResults[nodeValue]).sort((a, b) =>
      a.timestep > b.timestep ? 1 : -1,
    )
  }

  const isGivenEvent = props.prefixName === "given_event"

  const nodeSelectOptions = getNodeSelectOptions(props.nodesData)
  const timestepOptions = getTimestepOptions(nodeResults, props.lag)

  const getNodeSelectValue = () => {
    const fieldValue = form.getValues(`${props.prefixName}.node`)
    if (!fieldValue) return null

    const foundOption = nodeSelectOptions.find(
      (option) => graphViewGroupNodeLabelToSampleNode(option.label) === fieldValue,
    )
    return foundOption ?? null
  }

  const handleNodeSelectChange = (
    newValue: GroupNodeLabel | null,
    onChange: (value: string | null) => void,
  ) => {
    const parsedValue = graphViewGroupNodeLabelToSampleNode(newValue?.label) ?? null

    onChange(parsedValue)
  }

  return (
    <>
      <Grid item>
        <Controller
          control={form.control}
          name={`${props.prefixName}.node`}
          render={({ field }) => (
            <NodeSelect
              graph={props.graphData}
              label={isGivenEvent ? "Given event" : "event"}
              options={nodeSelectOptions}
              groupBy={undefined}
              value={getNodeSelectValue()}
              onChange={(newValue) => handleNodeSelectChange(newValue, field.onChange)}
              // @ts-ignore kk TODO: check if fixed on "@mui/material" > 5.15.14 version
              disableClearable
            />
          )}
        />
      </Grid>
      <Grid item>
        <Controller
          control={form.control}
          name={`${props.prefixName}.timestep`}
          render={({ field }) => (
            <Autocomplete
              options={timestepOptions}
              size="small"
              renderInput={(params) => (
                <TextField {...params} label={isGivenEvent ? "Given event date" : "Date"} />
              )}
              {...field}
              onChange={(_event, newValue) => field.onChange(newValue)}
            />
          )}
        />
      </Grid>
      <Grid item>
        <Controller
          control={form.control}
          name={`${props.prefixName}.operator`}
          render={({ field }) => (
            <FormControlLabel
              label="Less than"
              labelPlacement="start"
              sx={{ ml: 0 }}
              control={
                <Switch
                  checked={field.value === "<"}
                  onChange={(event) => field.onChange(event.target.checked ? "<" : ">")}
                />
              }
            />
          )}
        />
      </Grid>
      <Grid item>
        <BoundarySlider
          timestep={timestepValue}
          nodeResults={nodeResults}
          name={`${props.prefixName}.boundary`}
          showMinMax={props.showMinMaxBoundary}
        />
      </Grid>
    </>
  )
}

export default EventFields
